import React from "react";
import styles from "./SimplifiedGamepad.module.css";
import type {
  VirtualGamepadProps,
  MouseSimulationHandlers,
} from "../../constants/gamepad";
import { useVirtualGamepadInput } from "../../hooks/useVirtualGamepadInput";
import { ActionButtonGroup, Joystick, TopButtonContainer } from "../shared";
import LBIcon1x from "../../assets/icons/LB@1x.webp";
import LBIcon2x from "../../assets/icons/LB@2x.webp";
import LBIcon3x from "../../assets/icons/LB@3x.webp";
import LTIcon1x from "../../assets/icons/LT@1x.webp";
import LTIcon2x from "../../assets/icons/LT@2x.webp";
import LTIcon3x from "../../assets/icons/LT@3x.webp";
import RBIcon1x from "../../assets/icons/RB@1x.webp";
import RBIcon2x from "../../assets/icons/RB@2x.webp";
import RBIcon3x from "../../assets/icons/RB@3x.webp";
import RTIcon1x from "../../assets/icons/RT@1x.webp";
import RTIcon2x from "../../assets/icons/RT@2x.webp";
import RTIcon3x from "../../assets/icons/RT@3x.webp";
import { Button } from "../shared/Button";

interface SimplifiedGamepadProps extends MouseSimulationHandlers {
  onStateChange?: VirtualGamepadProps["onStateChange"];
  sendMessage?: VirtualGamepadProps["sendMessage"];
  onToggleMenu?: () => void;
  isActive?: boolean;
}

export const SimplifiedGamepad: React.FC<SimplifiedGamepadProps> = ({
  onStateChange,
  sendMessage,
  onToggleMenu,
  isActive = false,
  shouldSimulateMouseEvents,
  simulateMouseMovement,
  simulateMouseEvent,
  stopMouseSimulation,
}) => {
  const {
    isLeftJoystickActive,
    leftJoystickOuterRef,
    handleButtonTouchStart,
    handleButtonTouchEnd,
    handleButtonMouseDown,
    handleButtonMouseUp,
    handleLeftJoystickTouchStart,
    handleLeftJoystickTouchMove,
    handleLeftJoystickTouchEnd,
    handleLeftJoystickMouseDown,
    leftJoystickTransform,
  } = useVirtualGamepadInput({
    onStateChange,
    sendMessage,
    onToggleMenu,
    isActive,
    shouldSimulateMouseEvents,
    simulateMouseMovement,
    simulateMouseEvent,
    stopMouseSimulation,
  });

  if (!isActive) return null;

  return (
    <div className={styles.container}>
      <div className={styles.gamepadContainer}>
        <TopButtonContainer
          onTouchStart={handleButtonTouchStart}
          onTouchEnd={handleButtonTouchEnd}
          onMouseDown={handleButtonMouseDown}
          onMouseUp={handleButtonMouseUp}
        />
        <div className={styles.bottom}>
          <div className={styles.bottomLeft}>
            <Joystick
              isActive={isLeftJoystickActive}
              outerRef={leftJoystickOuterRef}
              onTouchStart={handleLeftJoystickTouchStart}
              onTouchMove={handleLeftJoystickTouchMove}
              onTouchEnd={handleLeftJoystickTouchEnd}
              onMouseDown={handleLeftJoystickMouseDown}
              transform={leftJoystickTransform}
            />
            <div className={styles.triggerButtonsContainer}>
              <Button
                buttonKey="l1"
                iconConfig={{
                  src1x: LBIcon1x,
                  src2x: LBIcon2x,
                  src3x: LBIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
              />
              <Button
                buttonKey="l2"
                iconConfig={{
                  src1x: LTIcon1x,
                  src2x: LTIcon2x,
                  src3x: LTIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
              />
            </div>
          </div>
          <div className={styles.bottomRight}>
            <div className={styles.triggerButtonsContainer}>
              <Button
                buttonKey="r1"
                iconConfig={{
                  src1x: RBIcon1x,
                  src2x: RBIcon2x,
                  src3x: RBIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
              />
              <Button
                buttonKey="r2"
                iconConfig={{
                  src1x: RTIcon1x,
                  src2x: RTIcon2x,
                  src3x: RTIcon3x,
                }}
                onTouchStart={(e, buttonKey) =>
                  handleButtonTouchStart(e, buttonKey!)
                }
                onTouchEnd={(e, buttonKey) =>
                  handleButtonTouchEnd(e, buttonKey!)
                }
                onMouseDown={(e, buttonKey) =>
                  handleButtonMouseDown(e, buttonKey!)
                }
                onMouseUp={(e, buttonKey) => handleButtonMouseUp(e, buttonKey!)}
              />
            </div>
            <ActionButtonGroup
              onTouchStart={handleButtonTouchStart}
              onTouchEnd={handleButtonTouchEnd}
              onMouseDown={handleButtonMouseDown}
              onMouseUp={handleButtonMouseUp}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
